import {Component, Element, h, Listen, Prop} from '@stencil/core';
import {getGuidedSearchCategory, pushToDataLayer} from '../util';
import type {GuidedSearchCompleted} from '../../guided/api';
import { ChipType, type GuidedChipEventDetail } from '../../guided2/subcomponents/chips-carousel/model';
import {makeLogger} from '../../../logger/logger';

const {
  //info,
  // debug,
  // warn,
  err
} = makeLogger({
  pathToCurrentFile: 'components/analytics-repoertine/guided-v2/uiaas-analytics-reporting-guided-v2.components.tsx',
});

@Component({
  tag: 'uiaas-search-analytics-reporting-guided-v2',
})
export class UiaasSearchAnalyticsReportingGuidedComponentv2 {
  /**
   * 'SE Main Site' for se.com public website,
   * 'Seamless' for myschneider
   */
    @Prop() readonly digitalPlatform?: string = 'SE Main Site'; // SVS-35914

    @Prop() readonly setup: string|object = '{}';

    private _parsedSetup?: object;

    private mapping(name, value) {
        if(typeof this.setup === 'object')
          return this.setup?.[name] ?? value;

        if(!this._parsedSetup) {
          try {
            this._parsedSetup = JSON.parse(JSON.stringify(this.setup));
            return this._parsedSetup?.[name] ?? value;
          } catch (e) {
            err('failed to parse GA mapping',this.setup);
            return {};
          }
        }else{
          return this._parsedSetup?.[name] ?? value;
        }
    };

    @Element() element: HTMLElement;


    private async getSearchKeyword() {
        try {
          const inputElement = await document?.querySelector('uiaas-guided-search-v2')?.getDatafluxUnsafe();
          return await inputElement?.root?.searchTerm ?? '';
        } catch (error) {
          return '';
        }
    }

    private readonly EVENTS_NAMES = {
      guidedSearchClick: 'guidedSearchClick',
      search: 'search',
      onSiteSearch: 'OnSiteSearch'
    };

    @Listen('guidedV2:CheckAvailability')
    guidedV2CheckAvailability(eventDetail: {
      searchTerm: string,
      quickLinkOpenUrl: string,
    } & CustomEvent) {

        pushToDataLayer({
          digital_platform: this.digitalPlatform,
          event_name: 'guidedSearchClick',
          $guidedSearchCategory: 'Guided Search: p&a',
          site_search_category: 'Guided Search',
          guided_search_keyword: eventDetail?.searchTerm,
          destination_url: eventDetail?.quickLinkOpenUrl,
        });

    }

    @Listen('guided:chipClicked')
    guidedV2FilterClickedEventHandler(event: GuidedChipEventDetail & CustomEvent) {
        if(event?.detail.chipType == ChipType.GuidedV2Sections){
          pushToDataLayer({
            event: this.EVENTS_NAMES.onSiteSearch,
            site_search_category: 'Guided Search',
            site_search_action: 'click_filter',
            site_search_label: `category:${event?.detail.label}`,
            search_location: 'Site Search',
          });
        }

        if(event?.detail.chipType == ChipType.GuidedV2Suggestions && !event?.detail.isPromoted) {
          pushToDataLayer({
            event: this.EVENTS_NAMES.guidedSearchClick,
            guidedSearchKeyword: event?.detail.label,
            guided_search_category: 'Guided Search: Most popular searches',
            site_search_label: 'Guided Search v2',
            search_location: 'Site Search',
            digital_platform: this.digitalPlatform
          });
        }
    }

    // TBD in events
    @Listen('guidedV2:SectionChanged')
    categoryClickedEventHandler(event: CustomEvent) {
        pushToDataLayer({
          event: this.EVENTS_NAMES.onSiteSearch,
          site_search_category: 'Guided Search',
          site_search_action: 'view_all',
          site_search_label: `category:${event.detail?.label}`,
          search_location: 'Site Search',
        });
    }

    @Listen('guidedV2:SearchCompleted')
    async searchCompletedEventHandler(event: GuidedSearchCompleted & CustomEvent) {
        const searchKeyword = await this.getSearchKeyword();
        if (event.detail.event === this.EVENTS_NAMES.guidedSearchClick) {

          pushToDataLayer({
            event: this.EVENTS_NAMES.guidedSearchClick,
            guided_search_keyword: searchKeyword,
            guided_search_category: `Guided Search:${event.detail.category}`,
            site_search_label: 'Guided Search v2',
            destination_url: event.detail.link,
            search_location: 'Site Search',
            digital_platform: this.digitalPlatform
          });

          pushToDataLayer({
            event: this.EVENTS_NAMES.search,
            site_search_category: `${getGuidedSearchCategory(event.target)}Guided Search`,
            guidedSearch: true,
            kw55: searchKeyword,
            refinedSearch: false,
            site_search_label: 'Guided Search v2',
            search_location: 'Site Search',
            digitalPlatform: this.digitalPlatform || 'SE Main Site'
          });
        }

    }

    @Listen('guidedV2:HistoryClicked')
    guidedV2HistoryClickedEventHandler(event: CustomEvent) {
        pushToDataLayer({
          event: this.EVENTS_NAMES.guidedSearchClick,
          guidedSearchKeyword: event.detail.term,
          guidedSearchCategory: 'Guided Search: Search history',
          site_search_label: 'Guided Search v2',
          search_location: 'Site Search',
          digitalPlatform: this.digitalPlatform || 'SE Main Site'
        });
    }

    @Listen('conversational-search:ga-cta-banner-clicked', { target: 'window' })
    conversationalSearchCtaBannerEventHandler(event: MouseEvent | CustomEvent) {
        if (event.detail.isGuided) {
          pushToDataLayer({
            event: 'genAI_search',
            search_keyword: event.detail.keyword,
            action: 'click',
            search_location: 'Site Search',
            label: [
              'isRefinement:false',
              'guidedSearchVersion:v2'
            ].join('###')
          });
        }
    }

    @Listen('uiaas:serachBarB2cClicked', { target: 'window' })
    searchBarB2cClickedEventHandler() {
        pushToDataLayer({
          event: 'OnSiteSearch',
          site_search_category: 'Search_bar',
          site_search_action: 'click',
          site_search_label: 'target:searchbar',
          site_search_keyword: '',
          search_location: 'Site Search',
        });
    }

    @Listen('guidedV2:SearchBarClicked', { target: 'window' })
    guidedV2SearchBarClickedEventHandler() {
        pushToDataLayer({
          event: 'OnSiteSearch',
          /**
           * For Help Center we need to override default value and set it to something different.
           * It should be configured locally. SVS-35965
           */
          site_search_category: this.mapping('search_bar_clicked::site_search_category','Search_bar'),
          site_search_action: 'click',
          site_search_label: 'target:searchbar',
          site_search_keyword: ''
        });
    }

    @Listen('guided2:ChipClicked', { target: 'window' })
    chipClicked(event: GuidedChipEventDetail & CustomEvent) {
        if(event?.detail?.isPromoted){
          pushToDataLayer({
            event: 'guidedSearchClick',
            guidedSearchCategory: 'Guided Search: promotions - popular-searches',
            guidedSearchKeyword: event?.detail?.label,
            search_location: 'Site Search',
            destination_url: `https://www.se.com/${event?.detail?.country}/${event?.detail?.language}/search/?q=${event?.detail?.label}`,
            site_search_category: 'Guided Search',
            site_search_label: 'Guided Search v2'
          });
        }
    }

    @Listen('guidedV2:SelectedSuggestionClicked', { target: 'window' })
    selectedSuggestionClicked(event: CustomEvent) {
        pushToDataLayer({
          event: 'guidedSearchClick',
          guidedSearchCategory: 'Guided Search: promotions - suggestions',
          search_location: 'Site Search',
          destination_url: event.detail.suggestionURL,
          site_search_category: 'Guided Search',
          site_search_label: 'Guided Search v2',
        });
        pushToDataLayer({
          event: 'search',
          site_search_category: 'Guided Search',
          guidedSearch: true,
          site_search_keyword: '',
          refinedSearch: false,
          site_search_results: '',
          site_search_label: 'Guided Search promotions',
          search_location: 'Site Search',
        });
    }

    render() {
        return <slot/>;
    }
}
